import axios from 'axios';

// Use the same API base URL as the main app
// Now using AWS API Gateway for both development and production
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://n98cpw2fo7.execute-api.ap-south-1.amazonaws.com/dev';

console.log('LargeFileUploadService using API_BASE_URL:', API_BASE_URL);
console.log('Current hostname:', window.location.hostname);

class LargeFileUploadService {
  constructor() {
    this.uploadProgress = new Map();
  }

  // Generate presigned URL for file upload
  async generatePresignedUrl(file, tripId = null) {
    try {
      const token = localStorage.getItem('adminToken');

      // Ensure PDF files have correct MIME type
      let fileType = file.type;
      if (/\.pdf$/i.test(file.name) && (!fileType || fileType === '')) {
        fileType = 'application/pdf';
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/upload/presigned-url`,
        {
          fileName: file.name,
          fileType: fileType,
          fileSize: file.size,
          tripId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error generating presigned URL:', error);
      throw new Error(error.response?.data?.error || 'Failed to generate upload URL');
    }
  }

  // Upload file directly to S3 using presigned URL
  async uploadToS3(file, presignedUrl, onProgress = null) {
    try {
      console.log('Uploading to S3 with URL:', presignedUrl);
      console.log('File details:', {
        name: file.name,
        type: file.type,
        size: file.size
      });

      // Use fetch instead of axios for better S3 compatibility
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-amz-acl': 'public-read'
        }
      });

      // Note: Progress tracking with fetch is limited, but we can simulate it
      if (onProgress) {
        onProgress(100); // Simulate completion
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('S3 upload failed:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
      }

      console.log('S3 upload successful:', response.status);
      return response;
    } catch (error) {
      console.error('Error uploading to S3:', error);

      // Parse S3 error response if available
      if (error.response?.data) {
        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(error.response.data, 'text/xml');
          const errorCode = xmlDoc.getElementsByTagName('Code')[0]?.textContent;
          const errorMessage = xmlDoc.getElementsByTagName('Message')[0]?.textContent;
          console.error('S3 Error Details:', {
            code: errorCode,
            message: errorMessage,
            fullResponse: error.response.data
          });
        } catch (parseError) {
          console.error('Could not parse S3 error response:', parseError);
        }
      }

      throw new Error('Failed to upload file to S3');
    }
  }

  // Upload large file with progress tracking
  async uploadLargeFile(file, tripId = null, onProgress = null) {
    try {
      console.log('Starting upload process for:', file.name);

      // Step 1: Get presigned URL
      console.log('Step 1: Getting presigned URL...');
      const { presignedUrl, fileUrl, s3Key } = await this.generatePresignedUrl(file, tripId);
      console.log('Presigned URL received:', presignedUrl);
      console.log('Target S3 Key:', s3Key);

      // Step 2: Skip CORS test for presigned URLs (they don't support HEAD requests)
      console.log('Step 2: Skipping CORS test for presigned URL (not needed for upload)');

      // Step 3: Upload to S3
      console.log('Step 3: Uploading to S3...');
      await this.uploadToS3(file, presignedUrl, onProgress);
      console.log('S3 upload completed successfully');

      return {
        success: true,
        fileUrl,
        s3Key,
        fileName: file.name
      };
    } catch (error) {
      console.error('Error uploading large file:', error);
      throw error;
    }
  }

  // Upload multiple large files
  async uploadMultipleFiles(files, tripId = null, onProgress = null) {
    const results = [];
    const totalFiles = files.length;
    let completedFiles = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        const result = await this.uploadLargeFile(file, tripId, (fileProgress) => {
          if (onProgress) {
            const overallProgress = Math.round(((completedFiles + (fileProgress / 100)) / totalFiles) * 100);
            onProgress(overallProgress, i, fileProgress);
          }
        });

        results.push({
          ...result,
          originalIndex: i,
          originalName: file.name
        });

        completedFiles++;
      } catch (error) {
        results.push({
          success: false,
          error: error.message,
          originalIndex: i,
          originalName: file.name
        });
        completedFiles++;
      }
    }

    return results;
  }

  // Check if file needs presigned URL upload (now all files use presigned URLs)
  needsPresignedUpload(file) {
    return true; // All files now use presigned URLs for consistency
  }


  // Get file size in human readable format
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Validate file before upload
  validateFile(file) {
    const errors = [];

    // Check file size
    const maxImageSize = 50 * 1024 * 1024; // 50MB
    const maxVideoSize = 100 * 1024 * 1024; // 100MB
    const maxPdfSize = 25 * 1024 * 1024; // 25MB

    if (file.type.startsWith('image/') && file.size > maxImageSize) {
      errors.push(`Image file too large: ${this.formatFileSize(file.size)}. Maximum size: 50MB`);
    }

    if (file.type.startsWith('video/') && file.size > maxVideoSize) {
      errors.push(`Video file too large: ${this.formatFileSize(file.size)}. Maximum size: 100MB`);
    }

    if (file.type === 'application/pdf' && file.size > maxPdfSize) {
      errors.push(`PDF file too large: ${this.formatFileSize(file.size)}. Maximum size: 25MB`);
    }

    // Check file type
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];
    const allowedVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/wmv', 'video/flv', 'video/webm', 'video/quicktime'];
    const allowedPdfTypes = ['application/pdf'];

    // Also check by file extension for PDF (in case MIME type is not set correctly)
    const isPdfByExtension = /\.pdf$/i.test(file.name);

    if (!allowedImageTypes.includes(file.type) &&
        !allowedVideoTypes.includes(file.type) &&
        !allowedPdfTypes.includes(file.type) &&
        !isPdfByExtension) {
      errors.push('Invalid file type. Allowed: JPG, PNG, GIF, WebP, HEIC, MP4, MOV, AVI, WMV, FLV, WebM, PDF');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default new LargeFileUploadService();
